"use client";

import { useState } from "react";
import { Mail, ArrowRight, Send } from "lucide-react";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    website: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    await new Promise(resolve => setTimeout(resolve, 1000));

    setSubmitted(true);
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <section className="py-24 bg-[rgb(var(--bg-section))] relative overflow-hidden" id="kontakt">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#1B3A60]/10 blur-[120px]" />
        </div>
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-gradient-to-br from-[rgb(var(--bg-surface))] to-[rgb(var(--bg-section))] border border-[#1B3A60]/30 rounded-2xl p-12">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1B3A60]/40 to-[rgb(var(--bg-surface))] border border-[#1B3A60]/40 flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-[#4A7AB8]" />
              </div>
              <h3 className="text-2xl font-bold text-[rgb(var(--text-primary))] mb-4">
                Vielen Dank für Ihre Anfrage zur Potenzial-Analyse!
              </h3>
              <p className="text-[rgb(var(--text-muted-strong))]">
                Wir melden uns zeitnah mit den ersten Ergebnissen und konkreten Chancen.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-[rgb(var(--bg-section))] relative overflow-hidden" id="kontakt">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-[10%] w-[500px] h-[500px] rounded-full bg-[#1B3A60]/8 blur-[120px]" />
        <div className="absolute bottom-20 left-[10%] w-[400px] h-[400px] rounded-full bg-[#3C2447]/8 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="max-w-2xl mx-auto">

          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#1B3A60]/20 to-[#3C2447]/20 border border-[#1B3A60]/30 rounded-full px-4 py-2 text-sm font-medium text-[#C9B37E] mb-4">
              <Send className="w-4 h-4" />
              Unverbindlich
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[rgb(var(--text-primary))] mb-4">
              Kostenlose Potenzial-Analyse sichern
            </h2>
            <p className="text-lg text-[rgb(var(--text-muted))]">
              Unverbindlich, ohne Verkaufsdruck. Wir prüfen Ihre Website und zeigen, wo Sie aktuell Kunden verlieren und wie groß Ihr Potenzial ist.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-gradient-to-br from-[rgb(var(--bg-surface))] to-[rgb(var(--bg-section))] border border-[#1B3A60]/20 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-[rgb(var(--text-muted-strong))] mb-2">Name *</label>
                <input
                  type="text"
                  id="name"
                  required
                  data-testid="input-name"
                  className="w-full px-4 py-3 bg-[rgb(var(--bg-base))] border border-[#1B3A60]/20 rounded-xl text-[rgb(var(--text-primary))] placeholder-[rgb(var(--text-muted-faint))] focus:ring-2 focus:ring-[#1B3A60]/50 focus:border-[#1B3A60]/50 outline-none transition-all"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[rgb(var(--text-muted-strong))] mb-2">E-Mail *</label>
                <input
                  type="email"
                  id="email"
                  required
                  data-testid="input-email"
                  className="w-full px-4 py-3 bg-[rgb(var(--bg-base))] border border-[#1B3A60]/20 rounded-xl text-[rgb(var(--text-primary))] placeholder-[rgb(var(--text-muted-faint))] focus:ring-2 focus:ring-[#1B3A60]/50 focus:border-[#1B3A60]/50 outline-none transition-all"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-[rgb(var(--text-muted-strong))] mb-2">Unternehmen</label>
                <input
                  type="text"
                  id="company"
                  data-testid="input-company"
                  className="w-full px-4 py-3 bg-[rgb(var(--bg-base))] border border-[#1B3A60]/20 rounded-xl text-[rgb(var(--text-primary))] placeholder-[rgb(var(--text-muted-faint))] focus:ring-2 focus:ring-[#1B3A60]/50 focus:border-[#1B3A60]/50 outline-none transition-all"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                />
              </div>
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-[rgb(var(--text-muted-strong))] mb-2">Website</label>
                <input
                  type="url"
                  id="website"
                  data-testid="input-website"
                  placeholder="https://"
                  className="w-full px-4 py-3 bg-[rgb(var(--bg-base))] border border-[#1B3A60]/20 rounded-xl text-[rgb(var(--text-primary))] placeholder-[rgb(var(--text-muted-faint))] focus:ring-2 focus:ring-[#1B3A60]/50 focus:border-[#1B3A60]/50 outline-none transition-all"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-medium text-[rgb(var(--text-muted-strong))] mb-2">Nachricht</label>
              <textarea
                id="message"
                rows={4}
                data-testid="input-message"
                className="w-full px-4 py-3 bg-[rgb(var(--bg-base))] border border-[#1B3A60]/20 rounded-xl text-[rgb(var(--text-primary))] placeholder-[rgb(var(--text-muted-faint))] focus:ring-2 focus:ring-[#1B3A60]/50 focus:border-[#1B3A60]/50 outline-none transition-all resize-none"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="button-submit-contact"
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#1B3A60] via-[#2D5A8C] to-[#1B3A60] text-[rgb(var(--text-on-accent))] font-bold px-8 py-4 rounded-xl hover:from-[#2D5A8C] hover:via-[#3A6A9C] hover:to-[#2D5A8C] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-xl shadow-[#1B3A60]/30 animate-glow-pulse"
            >
              {isSubmitting ? "Wird gesendet..." : "Kostenlose Potenzial-Analyse sichern"}
              <ArrowRight className="w-5 h-5" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
